import AppPaths from '@globals/paths'
import { colors } from '@globals/themes/defaultTheme'
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import MultiplicationT from '@shared/translations/MultiplicationT'
import { T } from '@transifex/react'

const cardStyle: SxProps = {
  p: 2,
  boxShadow: 'none',
  backgroundColor: colors.grayUltraLight,
  borderRadius: 4,
  border: 'none',
}

type AwardPointsRecipientAmountCardProps = {
  pointsAmount: number | null
  pointsRecipientCount: number
  totalSpend: number | null
  pointsBankAvailableBalance: number
  pointsBankName?: string
}
const AwardPointsRecipientAmountCard: React.FC<AwardPointsRecipientAmountCardProps> = ({
  pointsAmount,
  pointsRecipientCount,
  totalSpend,
  pointsBankAvailableBalance,
  pointsBankName,
}) => {
  return (
    <Paper sx={[cardStyle, { mt: 2 }]}>
      <Stack direction="row" useFlexGap sx={{ gap: 0.5 }}>
        <SavingsOutlinedIcon color="disabled" />
        <Stack>
          {pointsBankName && (
            <Typography>
              <T
                _str="Funded by: {pointsBankName}"
                pointsBankName={
                  <Link
                    id="btnFundedByPointsBankLink"
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={AppPaths.tenant.admin.pointsRules('bank')}>
                    {pointsBankName}
                  </Link>
                }
              />
            </Typography>
          )}
          <MultiplicationT
            factor1={
              pointsAmount === null ? (
                <T _str="Multiple point amounts" />
              ) : (
                <T
                  _str="{pointsAmountNum, plural, one {{pointsAmount} point} other {{pointsAmount} points}}"
                  pointsAmountNum={pointsAmount}
                  pointsAmount={pointsAmount.toLocaleString()}
                />
              )
            }
            factor2={
              <T
                _str="{recipientCountNum, plural, one {{recipientCount} recipient} other {{recipientCount} recipients}}"
                recipientCountNum={pointsRecipientCount}
                recipientCount={pointsRecipientCount.toLocaleString()}
              />
            }
            result={
              <T
                _str="{totalSpendNum, plural, one {{totalSpend} point used} other {{totalSpend} points used}}"
                totalSpendNum={totalSpend || 0}
                totalSpend={totalSpend?.toLocaleString() || '0'}
              />
            }
          />
          <Typography>
            <T
              _str="Remaining funds after sending = {remainingFunds}"
              remainingFunds={
                <T
                  _str="{remainingPointsNum, plural, one {{remainingPoints} point} other {{remainingPoints} points}}"
                  remainingPointsNum={pointsBankAvailableBalance - (totalSpend || 0)}
                  remainingPoints={(
                    pointsBankAvailableBalance - (totalSpend || 0)
                  ).toLocaleString()}
                />
              }
            />
          </Typography>
          <Typography variant="caption">
            <T _str="Awards are funded by point banks and don't impact your monthly point balance" />
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  )
}

export default AwardPointsRecipientAmountCard
